import React from 'react';
import { ContainerLayout, ContentLayout } from '@hyperflake/react-ui-library';
import { ProgressTabModal, ProgressTabProvider } from '@library/progress-tab';
import { AppNavbar } from 'modules/shared/features/navigation';
import InstallationSidebar from 'modules/shared/features/navigation/components/InstallationSidebar/InstallationSidebar';
import { Outlet } from 'react-router-dom';
import AppNavbarMobile from 'modules/shared/features/navigation/components/AppNavBarMobile/AppNavbarMobile';
import { CrossIcon } from '@icon/index';

// --- Hook: detect mobile (matches Tailwind lg breakpoint = 1024px) ---
function useIsMobile(breakpoint = 1024) {
    const [isMobile, setIsMobile] = React.useState<boolean>(() =>
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    );

    React.useEffect(() => {
        const mq = window.matchMedia(`(max-width:${breakpoint - 1}px)`);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [breakpoint]);

    return isMobile;
}

// --- Desktop Layout ---
function DesktopInstallationShell() {
    return (
        <ContainerLayout>
            <AppNavbar />
            <InstallationSidebar />
            <ContentLayout className="p-0">
                <Outlet />
            </ContentLayout>
        </ContainerLayout>
    );
}

// --- Mobile Sidebar Drawer ---
function MobileInstallationDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    React.useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    return (
        <div aria-hidden={!open} className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}>
            {/* Backdrop */}
            <div
                onClick={onClose}
                className={`absolute inset-0 bg-black/40 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
            />
            {/* Panel */}
            <div
                id="mobile-sidebar"
                role="dialog"
                aria-label="Sidebar"
                className={`absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white shadow-xl transition-transform ${
                    open ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex items-center justify-between border-b px-3 py-2">
                    <span className="font-semibold">Menu</span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close menu"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-50"
                    >
                        <CrossIcon size={18} />
                    </button>
                </div>
                <div className="h-[calc(100%-44px)] overflow-auto">
                    <InstallationSidebar />
                </div>
            </div>
        </div>
    );
}

// --- Mobile Layout ---
function MobileInstallationShell() {
    const [open, setOpen] = React.useState(false);

    return (
        <div className="flex min-h-screen flex-col">
            <AppNavbarMobile onMenuClick={() => setOpen((v) => !v)} isMenuOpen={open} setOpen={setOpen} />
            <MobileInstallationDrawer open={open} onClose={() => setOpen(false)} />
            <div className="flex-1 overflow-auto pt-[56px]">
                <Outlet />
            </div>
        </div>
    );
}

// --- Responsive Installation Layout ---
const InstallationLayout = () => {
    const isMobile = useIsMobile(1024);

    return (
        <ProgressTabProvider>
            {isMobile ? <MobileInstallationShell /> : <DesktopInstallationShell />}
            <ProgressTabModal />
        </ProgressTabProvider>
    );
};

export default InstallationLayout;
